import { Option } from "../../../../constants/common";

export const BILLED_STATUS_LIST: Option[] = [
  { value: "NOT_BILLED", label: "Not Billed" },
  { value: "PARTIALLY_BILLED", label: "Partially Billed" },
  { value: "BILLED", label: "Billed" },
];
export type BilledStatus = "NOT_BILLED" | "PARTIALLY_BILLED" | "BILLED";
export const BILLED_STATUS_MAPPER: Record<BilledStatus, string> = {
  NOT_BILLED: "Not Billed",
  PARTIALLY_BILLED: "Partially Billed",
  BILLED: "Billed",
};

// Default listing on Ready for Billing shows only "Not Billed" encounters.
export const DEFAULT_BILLED_STATUS: Option[] = [
  { value: "NOT_BILLED", label: "Not Billed" },
];

export const tabsData = [
  {
    value: "",
    label: "Ready For Billing",
  },
  {
    value: "invoice",
    label: "Invoices",
  },
  {
    value: "claims",
    label: "Claims",
  },
  {
    value: "era_eob",
    label: "ERA/EOB",
  },
];

// Invoice type selected from the "Create Invoice" dropdown. The chosen type is
// carried to the Create Invoice form via the `invoiceType` query param and sent
// in the SuperBill POST payload. Values mirror PatientCardPaymentCategoryEnum.
export const INVOICE_TYPE = {
  SELF_PAY: "SELF_PAY",
  OUT_OF_NETWORK: "OUT_OF_NETWORK",
  PR: "PR",
} as const;

export type InvoiceType = (typeof INVOICE_TYPE)[keyof typeof INVOICE_TYPE];

// Billing route segment (path index 4) the invoice form is rendered on. The
// form is shared by the create and the edit screen, so the segment tells them
// apart when deciding between the create and the update API.
export const INVOICE_ROUTE_SEGMENT = {
  FROM_ENCOUNTER: "invoice",
  ADD: "add-invoice",
  UPDATE: "update-invoice",
} as const;

// Index of the route segment above inside the billing pathname
// (/app/provider/billing/<segment>/...).
export const INVOICE_ROUTE_SEGMENT_INDEX = 4;

// Route segments on which the invoice form auto-populates its service details
// and codes from the selected encounter.
export const ENCOUNTER_PREFILL_ROUTE_SEGMENTS: string[] = [
  INVOICE_ROUTE_SEGMENT.FROM_ENCOUNTER,
  INVOICE_ROUTE_SEGMENT.ADD,
];

export const INVOICE_TYPE_OPTIONS: { value: InvoiceType; label: string }[] = [
  { value: INVOICE_TYPE.SELF_PAY, label: "Self Pay" },
  { value: INVOICE_TYPE.OUT_OF_NETWORK, label: "Out of Network" },
  { value: INVOICE_TYPE.PR, label: "Patient Responsibility (PR)" },
];

// Billed (Ready For Billing) statuses a visit must carry to be offered in the
// Create Invoice > Visit ID dropdown, per invoice type:
//  - Self Pay / Out of Network: the visit still has something left to invoice.
//  - Patient Responsibility: only a fully billed visit — its claim went out to
//    the payer in full, which is what leaves a patient responsibility behind.
export const INVOICE_TYPE_VISIT_BILLED_STATUS: Record<InvoiceType, string> = {
  [INVOICE_TYPE.SELF_PAY]: "NOT_BILLED,PARTIALLY_BILLED",
  [INVOICE_TYPE.OUT_OF_NETWORK]: "NOT_BILLED,PARTIALLY_BILLED",
  [INVOICE_TYPE.PR]: "BILLED,PARTIALLY_BILLED",
};

// Used when the Create Invoice form is opened without an invoice type.
export const DEFAULT_VISIT_BILLED_STATUS = "NOT_BILLED,PARTIALLY_BILLED";

// Invoice payment status returned by getAllSuperBillV2 as `superBillStatus`.
// Drives which row actions the invoice Action column exposes.
export const INVOICE_STATUS = {
  PENDING: "PENDING",
  UNPAID: "UNPAID",
  PARTIALLY_PAID: "PARTIALLY_PAID",
  PAID: "PAID",
  CANCELLED: "CANCELLED",
} as const;

// Claim `postingStatus` returned by getAllClaimV2. A claim sits in
// PARTIALLY_PAID while only some of its CPT lines have been posted from the
// ERA/EOB, which is the claim-worklist equivalent of "Partially Posted" on the
// ERA and EOB worklists.
export const CLAIM_POSTING_STATUS_PARTIALLY_PAID = "PARTIALLY_PAID";

// Hover message on the disabled "Create Invoice for PR" action: the patient
// responsibility is not final until every CPT line has been posted.
export const CLAIM_NOT_COMPLETELY_POSTED_MSG =
  "This claim is not completely posted";

// Action menu values for the invoice Action column. The values are the wire
// contract between the option builder (`getInvoiceActionOptions`) and the
// click handler (`useInvoiceRowActions`), so both surfaces stay in step.
export const INVOICE_ACTION = {
  PRINT: "ViewBill",
  VIEW: "view",
  EDIT: "modify",
  CREATE_CLAIM: "createClaim",
  SEND_WITH_LINK: "sendInvoiceViaEmail",
  SEND_WITHOUT_LINK: "sendInvoiceViaEmailWithoutPaymentLink",
  CANCEL: "cancel",
  PRINT_CMS: "printAsCMS1500Form",
  COLLECT_PAYMENT: "collectPayment",
  VOID_PAYMENT: "voidPayment",
  ARCHIVE: "archive",
} as const;

export type InvoiceAction =
  (typeof INVOICE_ACTION)[keyof typeof INVOICE_ACTION];

export const INVOICE_ACTION_LABEL = {
  PRINT: "Print Invoice",
  VIEW: "View Invoice",
  EDIT: "Edit Invoice",
  CANCEL: "Cancel Invoice",
  PRINT_CMS: "Print As CMS 1500 Form",
  COLLECT_PAYMENT: "Collect Payment",
} as const;

// Email labels flip to "Resend" once the invoice has already gone out.
export const INVOICE_EMAIL_LABEL = {
  SEND: "Send",
  RESEND: "Resend",
  WITH_LINK_SUFFIX: "Invoice via Email (Payment Link)",
  WITHOUT_LINK_SUFFIX: "Invoice via Email (Without Payment Link)",
} as const;

// A Patient Responsibility invoice is generated from a claim whose ERA/EOB has
// already been posted, so it is only a presentation of that posted data: every
// claim-derived field stays read-only and the invoice can no longer be edited
// from the Action column.
export const PR_INVOICE_LOCKED_HOVER_MSG =
  "A Patient Responsibility invoice cannot be edited after ERA/EOB posting";

export const INVOICE_VIEW_TITLE = "View Invoice";
export const PR_INVOICE_VIEW_TITLE = "View Invoice for Patient Responsibility";

export type InvoiceActionOption = {
  value: InvoiceAction;
  label: string;
  isDisabled?: boolean;
  hoverMsg?: string;
};

/**
 * An invoice list row as the Action column sees it. The grid supplies rows
 * loosely, so every field is optional; only the ones the actions read matter.
 */
export type InvoiceRow = Partial<SuperBillColumnsType>;

// Single source of truth for how an invoice (super bill) status is presented.
// Billing -> Invoices and Patient Chart -> Financials -> Invoices both render
// from these, so the same status can never show a different colour or label on
// the two screens.
export type SuperBillStatusVariant =
  "success" | "error" | "warning" | "info" | "default" | "neutral" | "pending";

export const SUPER_BILL_STATUS_VARIANT_MAP: Record<
  string,
  SuperBillStatusVariant
> = {
  ACCEPTED: "success",
  DRAFT: "warning",
  REJECTED: "error",
  SUBMITTED: "info",
  DENIED: "error",
  PAID: "success",
  CANCELLED: "error",
  PENDING: "pending",
  UNPAID: "pending",
  OVERDUE: "warning",
  PARTIALLY_PAID: "info",
  READY_FOR_CLAIM: "neutral",
};

export const DEFAULT_SUPER_BILL_STATUS_VARIANT: SuperBillStatusVariant =
  "default";

// CANCELLED is spelled "Canceled" in the Billing status filter and chip, so the
// label is overridden instead of being derived from the raw status value.
export const SUPER_BILL_STATUS_LABEL_MAP: Record<string, string> = {
  CANCELLED: "Canceled",
};

// The cancel API answers with "invoice cancelled successfully". The app spells
// the status "Canceled" everywhere it is shown, so the toast is raised from the
// front end instead of echoing the backend message.
export const INVOICE_CANCEL_SUCCESS_MSG = "Invoice canceled successfully";

export const ERA_EOB_SUB_TABS = [
  {
    label: "ERA Worklist",
    value: "era",
  },
  {
    label: "EOB Worklist",
    value: "eob",
  },
];

export type SuperBillColumnsType = {
  invoiceSentToPatient?: boolean;
  uuid: string;
  patientId: string;
  patientName: string;
  patientGender: string;
  patientDOB: string;
  placeOfService: string;
  serviceState: string;
  renderingProviderId: string;
  renderingProviderName: string;
  subTotal: number;
  active: boolean;
  archive: boolean;
  renderingProviderSpecialities: Record<string, string>;
  billDate: string;
  dueDate: string;
  patientBalance: number;
  prevPaid: number;
  superBillStatus: string | null;
  claimId?: string;
  invoiceType?: string;
  encounterId: string;
  encounterDate: string;
  appointmentDate: Date;
  dateOfService: Date;
  paymentMode?: string;
};
